import React from 'react';
import { useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Button, Separator, Spinner } from '@heroui/react';
import { IconClock as Clock } from '@tabler/icons-react';
import { NavBar } from '@/components/layout';
import { useAppSelector } from '@/stores/hooks';
import { useMobileNavigate } from '@/hooks/useMobileNavigate';

function formatDateTimeWithWeek(
  date: Date | string | null | undefined
): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const weekday = weekdays[d.getDay()];
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${month}.${day}（${weekday}）${hours}:${minutes}`;
}

function formatTimeRange(
  startTime: Date | string | null | undefined,
  endTime: Date | string | null | undefined
): string {
  const start = formatDateTimeWithWeek(startTime);
  const end = formatDateTimeWithWeek(endTime);
  if (!start && !end) return '';
  if (!start) return `- ${end}`;
  if (!end) return `${start} -`;
  return `${start} - ${end}`;
}

/**
 * 活动详情页面
 * 
 * 路由模式：/activities/:id
 */
export default function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { go, goWithBack } = useMobileNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const userId = user?.id;

  const { data: activity, isLoading, isError, error } = trpc.activities.get.useQuery(
    { id: id! },
    {
      enabled: !!id,
    }
  );

  if (isLoading) {
    return (
      <>
        <NavBar title="活动详情" showBack />
        <div className="flex items-center justify-center min-h-[60vh] pt-[var(--navbar-height,44px)]">
          <Spinner className="w-8 h-8" />
        </div>
      </>
    );
  }

  if (isError || !activity) {
    return (
      <>
        <NavBar title="活动详情" showBack />
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 pt-[var(--navbar-height,44px)]">
          <p className="text-danger mb-2">加载失败</p>
          <p className="text-sm text-default-500 text-center mb-4">
            {error?.message || '活动不存在'}
          </p>
          <Button onPress={() => go('/activities')}>返回活动列表</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <NavBar
        title={activity.title || '活动详情'}
        showBack
      />
      <div className="w-full pt-[var(--navbar-height,44px)]">
        {/* 1. 封面图 - 满宽，保持比例 */}
        {activity.coverImage && (
          <div className="w-full aspect-video overflow-hidden bg-default-100">
            <img
              src={activity.coverImage}
              alt={activity.title || '活动封面'}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* 2. 操作按钮 - 四个按钮，2x2 网格 */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center justify-center"
              onPress={() => goWithBack(`/activities/${id}/lottery-rules`)}
            >
              <span className="text-sm">抽奖安排</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center justify-center"
              onPress={() => goWithBack(`/activities/${id}/posters`)}
            >
              <span className="text-sm">活动海报</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center justify-center"
              onPress={() => goWithBack(`/activities/${id}/my-invitations`)}
            >
              <span className="text-sm">我的邀请</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center justify-center"
              onPress={() => goWithBack(`/activities/${id}/stats`)}
            >
              <span className="text-sm">活动统计</span>
            </Button>
          </div>

          {/* 进入线上活动页面按钮 */}
          {activity?.onlineLink && (
            <Button
              variant="primary"
              className="w-full py-3"
              onPress={() => {
                // onlineLink 约定为站内相对路径；用 SPA 内跳转替代 window.open，
                // 避免微信内新开页导致底部前进/后退工具条。
                const path = activity.onlineLink!.startsWith('/')
                  ? activity.onlineLink!
                  : `/${activity.onlineLink!}`;
                const url = new URL(path, window.location.origin);
                url.searchParams.set('activity', id!);
                if (userId) {
                  url.searchParams.set('inviter', userId);
                }
                const to = `${url.pathname}${url.search}${url.hash}`;
                goWithBack(to);
              }}
            >
              进入线上活动页面
            </Button>
          )}

          {/* 3. 分割线 */}
          <Separator />

          {/* 4. 活动时间 */}
          {(activity.startTime || activity.endTime) && (
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-default-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  <span className="text-default-500">活动时间：</span>
                  {formatTimeRange(activity.startTime, activity.endTime)}
                </p>
              </div>
            </div>
          )}

          {/* 5. 报名截止时间 */}
          {activity.signupEndTime && (
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-default-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  <span className="text-default-500">报名截止时间：</span>
                  {formatDateTimeWithWeek(activity.signupEndTime)}
                </p>
              </div>
            </div>
          )}

          {/* 6. 分割线 */}
          <Separator />

          {/* 7. 活动介绍 */}
          {activity.description && (
            <div className="space-y-2">
              <h2 className="text-base font-semibold">活动介绍</h2>
              {/* 与编辑页 Textarea 一致：保留换行与连续空行（HTML 默认会折叠空白符） */}
              <div
                className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words"
                style={{ textAlign: 'justify' }}
              >
                {activity.description}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
